import debounce from 'lodash.debounce';
import Paginator from 'primevue/paginator';
import TieredMenu from 'primevue/tieredmenu';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  PlantStatus,
  IPlantTagDTO,
  PlantListDTO,
  PlantsOrderBy,
  PlantType,
} from '@/../client-generator/generated-clients/plants';
import PlantCardV2 from './PlantCard/PlantCard.vue';
import welcomeClient from '@/core/services/welcome-service';
import { SET_APP_LOADING } from '@/core/store/action-types';
import DialogPlantCreate from '../DialogPlantCreate/DialogPlantCreate.vue';
import DialogPlantDuplicate from '../DialogPlantDuplicate/DialogPlantDuplicate.vue';
import DialogConfirmDeletePlant from '@/views/components/DialogConfirmDeletePlant/DialogConfirmDeletePlant.vue';
import i18n from '@/core/translations/i18n';

const tagAllKey = 'tmp-fe-tag-all-key';

enum PlantsSelectionCriteria {
  all = 'all',
  project = 'project',
  installed = 'installed',
  favourites = 'favourites',
  commissioned = 'commissioned',
}

type PageChangeEvent = {
  rows: number;
  page: number;
  first: number;
  pageCount: number;
};

const allTag = {
  value: i18n.global.t('AppResource.All'),
  key: tagAllKey,
  isImportant: true,
};

@Options({
  name: 'PlantsList',
  components: {
    Paginator,
    TieredMenu,
    PlantCardV2,
    DialogPlantCreate,
    DialogPlantDuplicate,
    DialogConfirmDeletePlant,
  },
  watch: {
    orderBy() {
      this.fetchPlants(true);
    },
  },
})
export default class PlantsList extends Vue {
  isVisibleCreateDialog = false;
  isPlantDuplicateDialogVisible = false;

  isEdit = false;
  pageSize = 12;
  pageNumber = 0;
  searchTerm = '';
  searchTermFinal = '';
  filterBy: IPlantTagDTO[] = [];
  selectedFilterBy: any = allTag.key;
  selectedPlantId: number | null = null;
  selectedPlantType: PlantType | null = null;
  orderBy: PlantsOrderBy = PlantsOrderBy.MODIFIED_DATE;

  myTags: IPlantTagDTO[] = [];

  plantsSelectionCriteria = PlantsSelectionCriteria.all;

  totalPlants: number = 0;
  plants: PlantListDTO[] = [];
  plantUnderDuplication: PlantListDTO | null = {} as any;

  isPausedFetchupdate = false;
  intervalFetchUpdate: any | null = null;

  isVisibleDeletePlantConfirm = false;
  tempPlant: PlantListDTO | null = null;

  wasSearchExecuted = false;

  get selectedPlants() {
    let filteredPlants = [];

    switch (this.plantsSelectionCriteria) {
      case PlantsSelectionCriteria.all:
        filteredPlants = this.plants;
        break;

      case PlantsSelectionCriteria.favourites:
        filteredPlants = this.plants.filter((p) => p.isFavourite === true);
        break;

      case PlantsSelectionCriteria.installed:
        filteredPlants = this.plants.filter(
          (p) => p.status === PlantStatus.INSTALLED
        );
        break;

      case PlantsSelectionCriteria.project:
        filteredPlants = this.plants.filter(
          (p) => p.status === PlantStatus.PROJECT
        );
        break;

      case PlantsSelectionCriteria.commissioned:
        filteredPlants = this.plants.filter(
          (p) => p.status === PlantStatus.COMMISSIONED
        );
        break;
    }

    return filteredPlants;
  }

  get hasPlants() {
    return this.selectedPlants.length > 0;
  }

  get menuItems() {
    return [
      {
        selectionCriteria: PlantsSelectionCriteria.all,
        label: this.$t('AppResource.Plants'),
        icon: 'icon-all-plants',
      },
      {
        selectionCriteria: PlantsSelectionCriteria.favourites,
        label: this.$t('general.favourites'),
        icon: 'icon-star',
      },
    ];
  }

  get filterByOptions() {
    const opts = [allTag, ...this.myTags].map((tag) => ({
      value: tag.key,
      label: tag.value,
    }));
    return opts;
  }

  get orderByOptions() {
    return [
      {
        label: this.$t('plants.recentlyModified'),
        value: PlantsOrderBy.MODIFIED_DATE,
      },
      {
        label: this.$t('plants.plantName'),
        value: PlantsOrderBy.PLANT_NAME,
      },
    ];
  }

  get isLoading() {
    return this.$store.state.isLoading;
  }

  get plantToDeleteName() {
    if (this.tempPlant) {
      return this.tempPlant.name;
    }

    return '';
  }

  created() {
    this.handleSearch = debounce(this.handleSearch, 500);
  }

  async mounted() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await welcomeClient.welcome();
    } catch {
      console.error('welcome failed');
    }

    await this.fetchPlants();
    await this.fetchPlantTags();
    this.$store.dispatch(SET_APP_LOADING, false);

    this.registerPeriodicFetchUpdates();
  }

  beforeUnmount() {
    this.unregisterPeriodicFetchUpdates();
  }

  async fetchPlants(activateLoader = false) {
    if (this.wasSearchExecuted) {
      this.wasSearchExecuted = false;
    }

    try {
      if (activateLoader) this.$store.dispatch(SET_APP_LOADING, true);
      let filterTagKeys: string[] = [];

      if (this.filterBy && this.filterBy.length > 0) {
        if (!this.filterBy.find((x) => x.key === tagAllKey)) {
          filterTagKeys = this.filterBy.map((x) => x.key ?? '');
        }
      }

      const plantsResult = await plantsClient.plantsList(
        this.pageNumber,
        this.pageSize,
        this.orderBy,
        this.orderBy == PlantsOrderBy.PLANT_NAME,
        filterTagKeys,
        this.searchTermFinal,
        this.plantsSelectionCriteria == PlantsSelectionCriteria.favourites
      );

      this.plants = plantsResult.data as PlantListDTO[];
      this.totalPlants = plantsResult.total as number;
    } catch (error) {
      console.log(error);
    } finally {
      if (activateLoader) this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async fetchPlantTags() {
    try {
      this.myTags = await plantsClient.tagsList();
    } catch (error) {
      console.log(error);
    }
  }

  registerPeriodicFetchUpdates() {
    this.intervalFetchUpdate = setTimeout(async () => {
      await this.doFetchUpdatesInternal();
      this.registerPeriodicFetchUpdates();
    }, this.$config.PLANT_REFRESH_INTERVAL);
  }

  private async doFetchUpdatesInternal() {
    if (this.isPausedFetchupdate) {
      return;
    }

    await this.fetchPlants();
    this.fetchPlantTags();
  }

  unregisterPeriodicFetchUpdates() {
    clearTimeout(this.intervalFetchUpdate);
  }

  toggleCreateDialog(isNew = true) {
    if (isNew) {
      this.isEdit = false;
      this.selectedPlantId = null;
    }
    this.isVisibleCreateDialog = !this.isVisibleCreateDialog;
  }

  handlePageChange(event: PageChangeEvent) {
    this.pageNumber = event.page;
    this.fetchPlants(true);
  }

  async handleTabClick(event: { index: number }) {
    this.setSelectionCriteria(
      event.index === 0
        ? PlantsSelectionCriteria.all
        : PlantsSelectionCriteria.favourites
    );
    this.pageNumber = 0;
    await this.fetchPlants(true);
  }

  async handleSearch(event: Event) {
    const target = event.target as HTMLInputElement;

    if (target) {
      this.searchTermFinal = target.value;
      await this.fetchPlants(true);
      this.wasSearchExecuted = true;
    }
  }

  onFilterByChange(data: { value: string }) {
    const tag = [allTag, ...this.myTags].find((t) => t.key === data.value);
    if (!tag) {
      this.filterBy = [];
      return;
    }

    this.filterBy = [tag];
    this.fetchPlants(true);
    this.selectedFilterBy = tag.key!;
  }

  setSelectionCriteria(selectionCriteria: PlantsSelectionCriteria) {
    this.plantsSelectionCriteria = selectionCriteria;
  }

  handleEditPlant(plant: PlantListDTO) {
    this.selectedPlantId = plant.desktopId ?? null;
    this.selectedPlantType = plant.plantType ?? null;
    this.isEdit = true;
    this.toggleCreateDialog(false);
  }

  toggleDeletePlantConfirm(plant: PlantListDTO) {
    this.tempPlant = plant;
    this.isVisibleDeletePlantConfirm = !this.isVisibleDeletePlantConfirm;
  }

  async handleDeletePlant() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.plantDELETE(this.tempPlant?.desktopId as number);
      this.$router.push({
        name: 'page-plants-hub',
      });
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
      this.tempPlant = null;
      this.refreshList();
    }
  }

  async handleDuplicatePlant(plant: PlantListDTO) {
    this.plantUnderDuplication = plant;
    this.isPlantDuplicateDialogVisible = true;
  }

  handleDuplicatePlantFull(plant: PlantListDTO) {
    alert(`Duplicate full: ${plant.name}`);
  }

  handleHideDialog() {
    this.isPlantDuplicateDialogVisible = false;
  }

  async refreshList() {
    this.$store.dispatch(SET_APP_LOADING, true);
    await this.fetchPlants();
    this.$router.push({
      name: 'page-plants-hub',
    });
    this.$store.dispatch(SET_APP_LOADING, false);
  }
}
